import { BaseSchemaFormComponent, SchemaModel } from '../base-schema-form/base-schema-form.component';
import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { shouldSuppressInvalidTypeErrors, validateRequired } from '@app/schema-form/utils/utils';
import { I18nService } from '@app/shared/utils/i18n.service';
import { isEmpty } from '@app/shared/utils/util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export const DEBOUNCE_TIME = 300;

@UntilDestroy()
@Component({
	selector: 'g-editor-schema-form',
	templateUrl: '../base-schema-form/base-schema-form.component.html',
	styleUrls: ['./editor-schema-form.component.scss'],
	standalone: false,
})
export class EditorSchemaFormComponent extends BaseSchemaFormComponent implements OnInit, OnChanges {
	protected readonly element: ElementRef<HTMLElement>;
	protected readonly i18n: I18nService;

	@Input() readonly product?: api.ProductDto;
	@Input() readonly initialValues?: SchemaModel;
	private readonly valueDebounce$ = new Subject<SchemaModel>();

	constructor() {
		super();
		this.element = inject<ElementRef<HTMLElement>>(ElementRef);
		this.i18n = inject(I18nService);
	}

	ngOnInit(): void {
		this.valueDebounce$
			.pipe(debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
			.subscribe((formValues) => {
				super.setModelAndEmitChange(formValues);
			});
	}

	setModelAndEmitChange(formValues: SchemaModel): void {
		this.valueDebounce$.next(formValues);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.product?.currentValue) {
			this.setProductSchema(changes.product.currentValue);
			this.setDomIdentifiers();
			this.schemaChange.emit(this.schema);
		}

		if (changes.initialValues?.isFirstChange() && changes.initialValues?.currentValue) {
			this.patchProductSchema(changes.initialValues.currentValue);
			this.setDomIdentifiers();
			this.schemaChange.emit(this.schema);
		}
	}

	emitErrorChange(errors: Record<string, unknown>[]): void {
		setTimeout(() => {
			const hasErrors = validateRequired(this.model, this.schema);
			if (shouldSuppressInvalidTypeErrors(errors)) {
				return this.errorChange.emit({ hasErrors });
			}
			this.errorChange.emit({ hasErrors: !isEmpty(errors) || hasErrors });
		}, DEBOUNCE_TIME);
	}

	private setProductSchema(product: api.ProductDto) {
		const schema = product?.metadata?.product_editor_schema_json || { properties: {} };
		this.schema = this.parseSchemaByLocale(schema);
		this.setIsRequiredForSchemaProperties(this.schema);
	}

	private patchProductSchema(values: SchemaModel): void {
		this.initializeModel(values);
	}
}
